<template>
  <v-container fluid>
    <luci-btn-icon :config="backBtnConfig" @callback="$router.push({ name: 'GameList' })"></luci-btn-icon>

    <v-alert v-if="error" :value="true" color="error" icon="error" class="error-container">
      <span class="error-span luci-text white--text">{{ error }}</span>
    </v-alert>

    <div v-if="loadingData" class="loading">
      <img src="../../assets/images/icons/loading-pmc.gif" alt="'Loading">
    </div>

    <v-layout v-show="!loadingData" wrap>
      <v-flex md6>
        <luci-form v-show="!loadingData" :config="formConfig" v-model="formData" @submit="submitForm" @reset="resetForm" @callback="$router.push({ name: 'GameList' })"></luci-form>
      </v-flex>

      <v-flex md6 :class="{ 'columnRightPadding': $vuetify.breakpoint.mdAndUp }">
        <!-- Game translations -->
        <template v-show="!loadingData">
          <h3 class="luci-subtitle">Translations</h3>

          <p v-if="languagesLoading" class="luci-text font-weight-bold">Loading languages...</p>
          <p v-else-if="languagesWithoutEnglish.length === 0" class="luci-text text-uppercase font-weight-bold">NO LANGUAGES</p>
          <template v-else>
            <p class="luci-text">Click on the language to change the translation.</p>
            <v-btn class="lang-btn" v-for="lang in languagesWithoutEnglish" :key="lang.id" @click="openTranslationModal(lang)">
              <div class="lang-btn-text font-weight-bold">{{ lang.name }}</div>
              <div class="lang-btn-text">{{ lang.code }}</div>
            </v-btn>
          </template>

          <v-divider></v-divider>
        </template>

        <!-- Game files -->
        <template v-show="!loadingData">
          <h3 class="luci-subtitle">Game Files</h3>
          <p class="luci-text">Add game file versions to a brand.</p>

          <p v-if="brandsLoading" data-test-key="loading-brands" class="luci-text font-weight-bold">Loading brands...</p>
          <p v-else-if="brands.length === 0" data-test-key="no-brands" class="luci-text text-uppercase font-weight-bold">NO BRANDS</p>
          <div class="brand-tabs" v-else>
            <v-tabs slider-color="primary" :value="getBrandIndex" show-arrows>
              <template v-for="(brand, index) in brands">
                <v-tab ripple :key="index" @change="selectBrandTab(brand)">{{ brand.name }}</v-tab>
                <v-tab-item :key="index" class="pa-3">
                  <luci-table :data-brand="brand.id" :loading="isLoadingGameFileVersion" :config="gameFileVersionsConfig" @add-row-callback="addGameFileVersion" @delete-row-callback="confirmDeleteGameFileVersion" @field-click-callback="gameFileVersionsClickCallback"></luci-table>
                  <paging name="game-file-list" :view="gameFileVersionsView" @page="gotoGameFileVersionPage"></paging>
                </v-tab-item>
              </template>
            </v-tabs>
          </div>

          <v-divider></v-divider>
        </template>

        <!-- Game thumbnails -->
        <thumbnail-list v-show="!loadingData" :game-id="gameId"></thumbnail-list>
      </v-flex>
    </v-layout>

    <translation-modal
      v-if="selectedLanguage"
      :open="showTranslationModal"
      :item="selectedLanguage"
      @close="closeTranslationModal"></translation-modal>

    <message-modal
      data-test-key="messageModal"
      :open="showMsgModal"
      :title="msgModal.title"
      :message="msgModal.message"
      @ok-callback="resetMsgModal"></message-modal>

    <confirm-modal
      :open="showConfirmModal"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :image="confirmModal.image"
      @yes-callback="confirmYesFunction"
      @no-callback="closeConfirmModal"></confirm-modal>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { isEmpty, filter, split, find } from 'lodash'

import variables from '../../helpers/variables'
import MessageModal from '../modal/MessageModal'
import ConfirmModal from '../modal/ConfirmModal'
import TranslationModal from './TranslationModal'
import Paging from '../shared/paging/Paging'
import ThumbnailList from './thumbnail/List'

export default {
  components: {
    MessageModal,
    TranslationModal,
    ConfirmModal,
    Paging,
    ThumbnailList
  },
  data () {
    return {
      gameId: split(this.$route.params.id, '/api/games/')[1],
      backBtnConfig: variables.backBtnIconOptions,
      showMsgModal: false,
      msgModal: {},
      confirmModal: {},
      showConfirmModal: false,
      confirmYesAction: '',
      modalItem: {},
      showTranslationModal: false,
      selectedLanguage: null
    }
  },
  computed: {
    ...mapGetters({
      error: 'game/update/error',
      isLoading: 'game/update/isLoading',
      updated: 'game/update/updated',
      deleteError: 'game/del/error',
      deleteLoading: 'game/del/isLoading',
      retrieved: 'game/update/retrieved',
      violations: 'game/update/violations',
      getRetrievedData: 'game/update/getRetrievedData',
      languages: 'game/update/languages',
      languagesLoading: 'game/update/languagesLoading',
      brands: 'game/update/brands',
      brandsLoading: 'game/update/brandsLoading',
      isLoadingGameFileVersion: 'game/update/isLoadingGameFileVersion',
      gameFileVersionError: 'game/update/gameFileVersionError',
      gameFileVersionsView: 'game/update/gameFileVersionsView',
      getRetrievedGameFileVersions: 'game/update/getRetrievedGameFileVersions',
      selectedBrandTab: 'game/update/selectedBrandTab',
      publishGameFileError: 'game/update/publishGameFileError'
    }),
    loadingData () {
      return this.isLoading || this.languagesLoading || this.brandsLoading
    },
    languagesWithoutEnglish () {
      return filter(this.languages, (x) => x.code !== 'en')
    },
    getBrandIndex: {
      get () {
        let result = null
        if (this.selectedBrandTab.id && this.brands.length > 0) {
          find(this.brands, (x, index) => {
            if (x.id === this.selectedBrandTab.id) {
              result = index
            }
          })
        }
        return result
      }
    },
    formData: {
      get () {
        return this.getRetrievedData
      },
      set (value) {
        this.updateRetrieved(value)
      }
    },
    formConfig () {
      return (
        {
          name: 'form-edit-game',
          title: 'Details',
          description: 'To update game please change the form below.',
          showButtonAsIcon: true,
          buttons: variables.formBtnIconOptions,
          fields: [
            {
              order: 1,
              element: 'input',
              required: true,
              name: 'name',
              label: 'name',
              inputConfig: { type: 'text', placeholder: 'Please enter game name', clearable: true }
            },
            {
              order: 2,
              element: 'textarea',
              required: true,
              name: 'description',
              label: 'description',
              textareaConfig: { placeholder: 'Please enter game description', clearable: true }
            },
            {
              order: 3,
              element: 'checkbox',
              required: false,
              name: 'active',
              label: 'active'
            }
          ]
        }
      )
    },
    gameFileVersionsConfig: {
      get: function () {
        return (
          {
            showSearch: false,
            showButtonEdit: false,
            showButtonDelete: true,
            showButtonAdd: false,
            hideActions: true,
            headers: [
              { text: 'Version', value: 'data.version' },
              { text: 'Published', value: 'data.publish-link' },
              { text: 'Game Link', value: 'data.play-link' },
              { text: '', value: 'actions', width: '50px' }
            ],
            items: [
              ...this.getRetrievedGameFileVersions,
              {
                data: {
                  showButtonAdd: 'upload',
                  showButtonDelete: false,
                  file: null
                },
                columns: [
                  {
                    field: 'upload',
                    value: null,
                    multipleFiles: false,
                    type: 'file',
                    text: 'Choose File',
                    colspan: 3
                  }
                ]
              }
            ],
            pagination: { sortBy: 'data.version' }
          }
        )
      }
    }
  },
  async created () {
    this.getAllLanguages()
    await this.retrieve(decodeURIComponent(this.$route.params.id))
    await this.getAllBrands()
    this.getBrandGameFileVersions({
      gameId: this.getRetrievedData.id,
      brandId: this.selectedBrandTab.id
    })
  },
  methods: {
    ...mapActions({
      retrieve: 'game/update/retrieve',
      update: 'game/update/update',
      updateRetrieved: 'game/update/updateRetrieved',
      getAllLanguages: 'game/update/getAllLanguages',
      getAllBrands: 'game/update/getAllBrands',
      getAllGameFileVersions: 'game/update/getAllGameFileVersions',
      getBrandGameFileVersions: 'game/update/getBrandGameFileVersions',
      uploadGameFileVersion: 'game/update/uploadGameFileVersion',
      removeGameFileVersion: 'game/update/removeGameFileVersion',
      setBrandTabSelected: 'game/update/setBrandTabSelected',
      publishGameFileVersion: 'game/update/publishGameFileVersion'
    }),
    closeConfirmModal () {
      this.showConfirmModal = false
      this.confirmModal = {}
      this.modalItem = {}
    },
    confirmYesFunction () {
      if (this.confirmYesAction === 'deleteGameFileVersion') {
        this.deleteGameFileVersion()
      }

      this.confirmYesAction = ''
    },
    resetForm () {
      this.retrieve(decodeURIComponent(this.$route.params.id))
    },
    buttonCallback (button) {
      if (button === 'cancel-btn') {
        this.$router.push({ name: 'GameList' })
      }
    },
    submitForm () {
      if (this.validateSettingActive(this.getRetrievedData)) {
        this.update().then(() => this.$router.push({ name: 'GameList' }))
      } else {
        this.msgModal.title = 'Activation Failed'
        this.msgModal.message = 'This game cannot be ACTIVE until it has a valid: <b>name</b> and <b>description</b>.'
        this.showMsgModal = true
      }
    },
    validateSettingActive (data) {
      if (data.active) {
        return data.name !== '' && data.description !== ''
      }
      return true
    },
    resetMsgModal () {
      this.showMsgModal = false
      this.msgModal = { title: null, message: null, image: null }
    },
    openTranslationModal (language) {
      this.selectedLanguage = language
      this.showTranslationModal = true
    },
    closeTranslationModal () {
      this.selectedLanguage = null
      this.showTranslationModal = false
    },
    selectBrandTab (brand) {
      this.setBrandTabSelected(brand)
      this.getBrandGameFileVersions({
        gameId: this.getRetrievedData.id,
        brandId: brand.id
      })
    },
    gameFileVersionsClickCallback (field, item) {
      if (field === 'publish-link') {
        this.publishGameFileVersion(item['@id']).then(() => {
          if (this.publishGameFileError) {
            this.msgModal.title = 'Publish Failed'
            this.msgModal.message = 'This game file version cannot be PUBLISHED, please try again or contact PM Connect for support.'
            this.showMsgModal = true
          }
        })
      }

      if (field === 'play-link') {
        window.open(item.path)
      }
    },
    confirmDeleteGameFileVersion (item) {
      this.confirmYesAction = 'deleteGameFileVersion'
      this.confirmModal = {
        title: 'Delete game file',
        message: `Do you want to delete this game file version?`
      }
      this.modalItem = item
      this.showConfirmModal = true
    },
    deleteGameFileVersion () {
      this.showConfirmModal = false
      this.confirmModal = {}

      this.removeGameFileVersion({
        gameId: this.getRetrievedData.id,
        brandId: this.selectedBrandTab.id,
        gameFileVersion: this.modalItem['@id']
      }).then(() => {
        this.modalItem = {}

        if (this.gameFileVersionError) {
          this.msgModal = {}
          this.showMsgModal = true
        }
      })
    },
    addGameFileVersion (item) {
      if (item.file !== null && !isEmpty(item.file)) {
        this.uploadGameFileVersion({
          game: this.getRetrievedData['@id'],
          brand: this.selectedBrandTab['@id'],
          file: item.file[0]
        })
          .then(() => {
            if (this.gameFileVersionError) {
              this.msgModal = { message: this.gameFileVersionError }
              this.showMsgModal = true
            }
          })
      } else {
        this.msgModal = { message: 'Please upload a game file.' }
        this.showMsgModal = true
      }
    },
    gotoGameFileVersionPage (page) {
      this.getBrandGameFileVersions({
        gameId: this.getRetrievedData.id,
        brandId: this.selectedBrandTab.id,
        page: this.gameFileVersionsView['hydra:' + page]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .columnRightPadding {
    padding-left: 3rem;
  }
</style>
